import { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { CalculationInput, CalculationResult } from '@/types';
import { calculateMap } from '@/features/calculator/logic/calculationLogic';
import { parseDateString } from '@/utils';
import { getCurrentLocation, reverseGeocode } from '@/utils/location';
import { analyticsService } from '@/services/AnalyticsService';
import { logger } from '@/lib/logger';

// Create default calculation result
const getDefaultCalculationResult = (): CalculationResult => {
    const defaultInner = { sign: "?", number: 0, age: 0 };
    const defaultSurrounding = { sign: "?", number: 0, gematria: 0 };
    const defaultProfile = { profileNumber: 0, profileSign: "?", zodiacPosition: 0 };
    const defaultNumerology = {
        dayNumber: 0, monthNumber: 0, yearNumber: 0, totalNumber: 0,
        totalFromAllDigits: 0, firstNumber: 0, familyNumber: 0, totalNameNumber: 0,
        eheviNumber: 0, itzurimNumber: 0, eheviInnerSumFirst: 0, eheviInnerSumFamily: 0,
        itzurimInnerSumFirst: 0, itzurimInnerSumFamily: 0, fullInnerSum: 0,
        eheviInnerSum: 0, itzurimInnerSum: 0, full2InnerSum: 0, hamashaOuterSum: 0, hamashaInnerSum: 0
    };

    return {
        innerSign: defaultInner,
        surroundingSign: defaultSurrounding,
        profile: defaultProfile,
        numerology: defaultNumerology,
        tur: { value: '-', label: 'לא זמין' }
    };
};

interface CalculatorCacheData {
    input: CalculationInput;
    result: CalculationResult;
    timestamp: number;
}

const CACHE_STORAGE_KEY = 'spirit_calculator_cache';

export const getStoredCalculatorCache = (): CalculatorCacheData | null => {
    if (typeof window === 'undefined') return null;
    try {
        const raw = localStorage.getItem(CACHE_STORAGE_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        if (parsed && parsed.input && parsed.result) return parsed;
        return null;
    } catch {
        return null;
    }
};

export const saveStoredCalculatorCache = (input: CalculationInput, result: CalculationResult) => {
    if (typeof window === 'undefined') return;
    try {
        const data: CalculatorCacheData = {
            input,
            result,
            timestamp: Date.now(),
        };
        localStorage.setItem(CACHE_STORAGE_KEY, JSON.stringify(data));
        localStorage.setItem('calculatorInput', JSON.stringify(input));
    } catch (err) {
        console.error('Failed to save calculation cache:', err);
    }
};

export const useShabtaiCalculator = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const hasHydrated = useRef(false);

    // Initial check of cache for instant Turn 0 hydration
    const initialCache = useRef<CalculatorCacheData | null>(getStoredCalculatorCache());

    const [input, setInput] = useState<CalculationInput>(() => {
        // 1. Check for URL Hydration (Priority)
        const fn = searchParams.get('fn');
        const ln = searchParams.get('ln');
        const mn = searchParams.get('mn');
        const bd = searchParams.get('bd');

        if (fn || ln || mn || bd) {
            return {
                firstName: fn || '',
                familyName: ln || '',
                motherName: mn || '',
                birthDate: bd || '', // ISO YYYY-MM-DD expected
                birthTime: '',
                cityName: '',
                isLocationDetected: false
            };
        }

        // 2. Priority: Synced calculation cache
        if (initialCache.current?.input && (initialCache.current.input.firstName || initialCache.current.input.birthDate)) {
            return initialCache.current.input;
        }

        // 3. Fallback to legacy LocalStorage
        const saved = localStorage.getItem('calculatorInput');
        return saved ? JSON.parse(saved) : {
            firstName: '',
            motherName: '',
            familyName: '',
            birthDate: '',
            birthTime: '',
            lat: undefined,
            lng: undefined,
            cityName: '',
            isLocationDetected: false
        };
    });

    // Handle URL cleanup (Consume & Clear)
    useEffect(() => {
        if (hasHydrated.current) return;

        const fn = searchParams.get('fn');
        const ln = searchParams.get('ln');
        const mn = searchParams.get('mn');
        const bd = searchParams.get('bd');
        const results = searchParams.get('results');

        // Tighten Hydration Rules: 
        // 1. firstName (fn) is MANDATORY
        // 2. Plus at least one of (ln, mn, bd)
        const hasMandatoryName = Boolean(fn && fn.trim() !== '');
        const hasSecondaryData = Boolean((ln && ln.trim() !== '') || (mn && mn.trim() !== '') || (bd && bd.trim() !== ''));
        const hasForcedResults = Boolean(results && (results === 'true' || results === '1'));

        if (hasMandatoryName && (hasSecondaryData || hasForcedResults)) {
            const newParams = new URLSearchParams(searchParams);
            ['fn', 'ln', 'mn', 'bd', 'results'].forEach(key => newParams.delete(key));
            
            // Immediately clear URL to prevent session conflicts
            setSearchParams(newParams, { replace: true });
            hasHydrated.current = true;

            const isDataValid = hasMandatoryName && hasSecondaryData;

            analyticsService.track('magic_link_hydration', { 
                is_success: isDataValid,
                is_results_forced: !!results,
                firstName: fn,
                familyName: ln,
                motherName: mn,
                birthDate: bd
            });
        }
    }, [searchParams, setSearchParams]);

    // Handle automatic location detection if none exists
    useEffect(() => {
        const detectLocation = async () => {
            if (!input.lat && !input.lng && (!input.cityName || input.cityName === '')) {
                const coords = await getCurrentLocation();
                if (coords) {
                    const geo = await reverseGeocode(coords.lat, coords.lon);
                    if (geo) {
                        setInput(prev => ({
                            ...prev,
                            lat: geo.lat,
                            lng: geo.lon,
                            cityName: geo.name,
                            timezone: geo.timezone,
                            isLocationDetected: true
                        }));
                    }
                }
            }
        };

        detectLocation();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        try {
            localStorage.setItem('calculatorInput', JSON.stringify(input));
        } catch (err) {
            console.error('Failed to sync calculatorInput:', err);
        }
    }, [input]);

    // Instant synchronous Turn 0 hydration of result
    const [result, setResult] = useState<CalculationResult>(() => {
        if (initialCache.current?.result && initialCache.current?.input) {
            const cachedIn = initialCache.current.input;
            if (
                cachedIn.firstName === input.firstName &&
                cachedIn.birthDate === input.birthDate &&
                (cachedIn.motherName || '') === (input.motherName || '')
            ) {
                logger.info('[Calculator Engine] Turn-0 Synchronous Hydration Success:', {
                    firstName: cachedIn.firstName,
                    motherName: cachedIn.motherName,
                    surroundingSign: initialCache.current.result.surroundingSign.sign,
                    innerSign: initialCache.current.result.innerSign.sign,
                });
                return initialCache.current.result;
            }
        }
        return getDefaultCalculationResult();
    });

    const [isCalculating, setIsCalculating] = useState(false);

    useEffect(() => {
        // Standardization & Preparation
        const calcInput = { ...input };

        // If no name and no birthDate, clear result immediately
        if (!input.birthDate && !input.firstName) {
            setResult(getDefaultCalculationResult());
            setIsCalculating(false);
            return;
        }

        if (input.birthDate && input.birthDate !== '') {
            const standardizedDate = parseDateString(input.birthDate);
            if (standardizedDate) {
                calcInput.birthDate = standardizedDate;
            }
        }

        // Fast Path: Programmatic input runs instantly (0ms) without debounce delay
        if (input.isProgrammatic) {
            try {
                const calculationResult = calculateMap(calcInput);
                logger.info('[Calculator Engine] Instant (0ms) Programmatic Calculation:', {
                    firstName: calcInput.firstName,
                    motherName: calcInput.motherName,
                    birthDate: calcInput.birthDate,
                    surroundingSign: calculationResult.surroundingSign.sign,
                    innerSign: calculationResult.innerSign.sign,
                });
                setResult(calculationResult);
                saveStoredCalculatorCache(input, calculationResult);
            } catch (error) {
                console.error('Calculation error (programmatic):', error);
                setResult(getDefaultCalculationResult());
            }
            setIsCalculating(false);
            return;
        }

        // Debounced Path for manual user typing in forms
        setIsCalculating(true);
        const timer = setTimeout(() => {
            try {
                const calculationResult = calculateMap(calcInput);
                logger.info('[Calculator Engine] Debounced (500ms) User Typing Calculation:', {
                    firstName: calcInput.firstName,
                    motherName: calcInput.motherName,
                    birthDate: calcInput.birthDate,
                    surroundingSign: calculationResult.surroundingSign.sign,
                    innerSign: calculationResult.innerSign.sign,
                });
                setResult(calculationResult);
                saveStoredCalculatorCache(input, calculationResult);
            } catch (error) {
                console.error('Calculation error (debounced):', error);
                setResult(getDefaultCalculationResult());
            }
            setIsCalculating(false);
        }, 500);

        return () => clearTimeout(timer);
    }, [input]);

    return { input, setInput, result, isCalculating };
};
